import React, { useState, useEffect, useMemo } from 'react';
import { GitCompare, Copy, Download, Shuffle, Eye, Settings, ArrowLeftRight, CheckCircle, XCircle } from 'lucide-react';

interface ComparisonMethod {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface ComparisonOptions {
  caseSensitive: boolean;
  ignoreWhitespace: boolean;
  ignoreEmptyLines: boolean;
  trimLines: boolean;
  wordLevel: boolean;
  characterLevel: boolean;
  showLineNumbers: boolean;
  highlightChanges: boolean;
  contextLines: number;
  ignoreLeadingWhitespace: boolean;
  ignoreTrailingWhitespace: boolean;
}

interface DifferenceResult {
  originalText: string;
  modifiedText: string;
  differences: Difference[];
  statistics: ComparisonStats;
  formattedOutput: string;
}

interface Difference {
  type: 'added' | 'removed' | 'modified' | 'unchanged';
  lineNumber: number;
  originalLine?: string;
  modifiedLine?: string;
  content: string;
  context?: string;
}

interface ComparisonStats {
  totalLines: number;
  addedLines: number;
  removedLines: number;
  modifiedLines: number;
  unchangedLines: number;
  addedCharacters: number;
  removedCharacters: number;
  similarityPercentage: number;
  wordDifferences: number;
  characterDifferences: number;
}

const TextDifferenceChecker: React.FC = () => {
  const [originalText, setOriginalText] = useState('');
  const [modifiedText, setModifiedText] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('unified');
  const [result, setResult] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showDifferences, setShowDifferences] = useState(false);

  const [options, setOptions] = useState<ComparisonOptions>({
    caseSensitive: true,
    ignoreWhitespace: false,
    ignoreEmptyLines: false,
    trimLines: false,
    wordLevel: false,
    characterLevel: false,
    showLineNumbers: true,
    highlightChanges: true,
    contextLines: 3,
    ignoreLeadingWhitespace: false,
    ignoreTrailingWhitespace: false
  });

  const comparisonMethods: ComparisonMethod[] = [
    {
      id: 'unified',
      name: 'Unified Diff',
      description: 'Standard unified diff format',
      icon: <GitCompare className="w-4 h-4" />,
      color: 'blue'
    },
    {
      id: 'sideBySide',
      name: 'Side by Side',
      description: 'Compare texts side by side',
      icon: <ArrowLeftRight className="w-4 h-4" />,
      color: 'green'
    },
    {
      id: 'inline',
      name: 'Inline Changes',
      description: 'Show changes within lines',
      icon: <GitCompare className="w-4 h-4" />,
      color: 'purple'
    },
    {
      id: 'wordDiff',
      name: 'Word Differences',
      description: 'Compare word by word',
      icon: <GitCompare className="w-4 h-4" />,
      color: 'red'
    },
    {
      id: 'characterDiff',
      name: 'Character Differences',
      description: 'Compare character by character',
      icon: <GitCompare className="w-4 h-4" />,
      color: 'yellow'
    },
    {
      id: 'contextDiff',
      name: 'Context Diff',
      description: 'Show differences with context',
      icon: <GitCompare className="w-4 h-4" />,
      color: 'indigo'
    },
    {
      id: 'summary',
      name: 'Summary Only',
      description: 'Show only statistics',
      icon: <Eye className="w-4 h-4" />,
      color: 'pink'
    },
    {
      id: 'additions',
      name: 'Additions Only',
      description: 'Show only added content',
      icon: <CheckCircle className="w-4 h-4" />,
      color: 'orange'
    },
    {
      id: 'deletions',
      name: 'Deletions Only',
      description: 'Show only removed content',
      icon: <XCircle className="w-4 h-4" />,
      color: 'teal'
    },
    {
      id: 'modifications',
      name: 'Modifications Only',
      description: 'Show only changed lines',
      icon: <GitCompare className="w-4 h-4" />,
      color: 'cyan'
    },
    {
      id: 'whitespace',
      name: 'Whitespace Changes',
      description: 'Focus on whitespace differences',
      icon: <Settings className="w-4 h-4" />,
      color: 'emerald'
    },
    {
      id: 'semantic',
      name: 'Semantic Diff',
      description: 'Intelligent content comparison',
      icon: <Eye className="w-4 h-4" />,
      color: 'violet'
    }
  ];

  const performComparison = (original: string, modified: string, method: string, opts: ComparisonOptions): DifferenceResult => {
    const originalLines = preprocessText(original, opts).split('\n');
    const modifiedLines = preprocessText(modified, opts).split('\n');
    
    const differences = computeDifferences(originalLines, modifiedLines, opts);
    const statistics = calculateStatistics(originalLines, modifiedLines, differences);
    
    let formattedOutput = '';
    
    switch (method) {
      case 'unified':
        formattedOutput = generateUnifiedDiff(differences, opts);
        break;
      case 'sideBySide':
        formattedOutput = generateSideBySideDiff(originalLines, modifiedLines, differences, opts);
        break;
      case 'inline':
        formattedOutput = generateInlineDiff(differences, opts);
        break;
      case 'wordDiff':
        formattedOutput = generateWordDiff(original, modified, opts);
        break;
      case 'characterDiff':
        formattedOutput = generateCharacterDiff(original, modified, opts);
        break;
      case 'contextDiff':
        formattedOutput = generateContextDiff(differences, opts);
        break;
      case 'summary':
        formattedOutput = generateSummary(statistics);
        break;
      case 'additions':
        formattedOutput = generateAdditionsOnly(differences, opts);
        break;
      case 'deletions':
        formattedOutput = generateDeletionsOnly(differences, opts);
        break;
      case 'modifications':
        formattedOutput = generateModificationsOnly(differences, opts);
        break;
      case 'whitespace':
        formattedOutput = generateWhitespaceDiff(original, modified, opts);
        break;
      case 'semantic':
        formattedOutput = generateSemanticDiff(original, modified, opts);
        break;
      default:
        formattedOutput = generateUnifiedDiff(differences, opts);
    }

    return {
      originalText: original,
      modifiedText: modified,
      differences,
      statistics,
      formattedOutput
    };
  };

  const preprocessText = (text: string, opts: ComparisonOptions): string => {
    let processed = text;
    
    if (!opts.caseSensitive) {
      processed = processed.toLowerCase();
    }
    
    if (opts.trimLines) {
      processed = processed.split('\n').map(line => line.trim()).join('\n');
    }
    
    if (opts.ignoreLeadingWhitespace) {
      processed = processed.split('\n').map(line => line.replace(/^\s+/, '')).join('\n');
    }
    
    if (opts.ignoreTrailingWhitespace) {
      processed = processed.split('\n').map(line => line.replace(/\s+$/, '')).join('\n');
    }
    
    if (opts.ignoreWhitespace) {
      processed = processed.replace(/\s+/g, ' ');
    }
    
    if (opts.ignoreEmptyLines) {
      processed = processed.split('\n').filter(line => line.trim() !== '').join('\n');
    }
    
    return processed;
  };

  const computeDifferences = (originalLines: string[], modifiedLines: string[], opts: ComparisonOptions): Difference[] => {
    const differences: Difference[] = [];
    const lcs = longestCommonSubsequence(originalLines, modifiedLines);
    
    let originalIndex = 0;
    let modifiedIndex = 0;
    let lineNumber = 1;
    
    while (originalIndex < originalLines.length || modifiedIndex < modifiedLines.length) {
      const originalLine = originalLines[originalIndex];
      const modifiedLine = modifiedLines[modifiedIndex];
      
      if (originalIndex < originalLines.length && modifiedIndex < modifiedLines.length) {
        if (originalLine === modifiedLine) {
          differences.push({
            type: 'unchanged',
            lineNumber,
            originalLine,
            modifiedLine,
            content: originalLine
          });
          originalIndex++;
          modifiedIndex++;
        } else if (isInLCS(originalLine, originalIndex, lcs)) {
          differences.push({
            type: 'added',
            lineNumber,
            modifiedLine,
            content: modifiedLine
          });
          modifiedIndex++;
        } else if (isInLCS(modifiedLine, modifiedIndex, lcs)) {
          differences.push({
            type: 'removed',
            lineNumber,
            originalLine,
            content: originalLine
          });
          originalIndex++;
        } else {
          differences.push({
            type: 'modified',
            lineNumber,
            originalLine,
            modifiedLine,
            content: `- ${originalLine}\n+ ${modifiedLine}`
          });
          originalIndex++;
          modifiedIndex++;
        }
      } else if (originalIndex < originalLines.length) {
        differences.push({
          type: 'removed',
          lineNumber,
          originalLine,
          content: originalLine
        });
        originalIndex++;
      } else {
        differences.push({
          type: 'added',
          lineNumber,
          modifiedLine,
          content: modifiedLine
        });
        modifiedIndex++;
      }
      
      lineNumber++;
    }
    
    return differences;
  };

  const longestCommonSubsequence = (arr1: string[], arr2: string[]): Set<string> => {
    const dp: number[][] = Array(arr1.length + 1).fill(null).map(() => Array(arr2.length + 1).fill(0));
    
    for (let i = 1; i <= arr1.length; i++) {
      for (let j = 1; j <= arr2.length; j++) {
        if (arr1[i - 1] === arr2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }
    
    const lcs = new Set<string>();
    let i = arr1.length, j = arr2.length;
    
    while (i > 0 && j > 0) {
      if (arr1[i - 1] === arr2[j - 1]) {
        lcs.add(arr1[i - 1]);
        i--;
        j--;
      } else if (dp[i - 1][j] > dp[i][j - 1]) {
        i--;
      } else {
        j--;
      }
    }
    
    return lcs;
  };

  const isInLCS = (line: string, index: number, lcs: Set<string>): boolean => {
    return lcs.has(line);
  };

  const calculateStatistics = (originalLines: string[], modifiedLines: string[], differences: Difference[]): ComparisonStats => {
    const addedLines = differences.filter(d => d.type === 'added').length;
    const removedLines = differences.filter(d => d.type === 'removed').length;
    const modifiedLinesCount = differences.filter(d => d.type === 'modified').length;
    const unchangedLines = differences.filter(d => d.type === 'unchanged').length;
    
    const originalText = originalLines.join('\n');
    const modifiedText = modifiedLines.join('\n');
    
    const addedCharacters = modifiedText.length - originalText.length > 0 ? modifiedText.length - originalText.length : 0;
    const removedCharacters = originalText.length - modifiedText.length > 0 ? originalText.length - modifiedText.length : 0;
    
    const totalLines = Math.max(originalLines.length, modifiedLines.length);
    const similarityPercentage = totalLines > 0 ? Math.round((unchangedLines / totalLines) * 100) : 100;
    
    const wordDifferences = calculateWordDifferences(originalText, modifiedText);
    const characterDifferences = calculateCharacterDifferences(originalText, modifiedText);
    
    return {
      totalLines,
      addedLines,
      removedLines,
      modifiedLines: modifiedLinesCount,
      unchangedLines,
      addedCharacters,
      removedCharacters,
      similarityPercentage,
      wordDifferences,
      characterDifferences
    };
  };

  const calculateWordDifferences = (original: string, modified: string): number => {
    const originalWords = original.split(/\s+/).filter(w => w);
    const modifiedWords = modified.split(/\s+/).filter(w => w);
    
    const maxLength = Math.max(originalWords.length, modifiedWords.length);
    let differences = 0;
    
    for (let i = 0; i < maxLength; i++) {
      if (originalWords[i] !== modifiedWords[i]) {
        differences++;
      }
    }
    
    return differences;
  };

  const calculateCharacterDifferences = (original: string, modified: string): number => {
    const maxLength = Math.max(original.length, modified.length);
    let differences = 0;
    
    for (let i = 0; i < maxLength; i++) {
      if (original[i] !== modified[i]) {
        differences++;
      }
    }
    
    return differences;
  };

  const generateUnifiedDiff = (differences: Difference[], opts: ComparisonOptions): string => {
    let output = '--- Original\n+++ Modified\n';
    
    differences.forEach((diff, index) => {
      const lineNum = opts.showLineNumbers ? `${diff.lineNumber}: ` : '';
      
      switch (diff.type) {
        case 'added':
          output += `+${lineNum}${diff.content}\n`;
          break;
        case 'removed':
          output += `-${lineNum}${diff.content}\n`;
          break;
        case 'modified':
          output += `${lineNum}${diff.content}\n`;
          break;
        case 'unchanged':
          if (opts.contextLines > 0) {
            output += ` ${lineNum}${diff.content}\n`;
          }
          break;
      }
    });
    
    return output;
  };

  const generateSideBySideDiff = (originalLines: string[], modifiedLines: string[], differences: Difference[], opts: ComparisonOptions): string => {
    let output = 'Original\t\t\t\tModified\n';
    output += '--------\t\t\t\t--------\n';
    
    const maxLines = Math.max(originalLines.length, modifiedLines.length);
    
    for (let i = 0; i < maxLines; i++) {
      const originalLine = originalLines[i] || '';
      const modifiedLine = modifiedLines[i] || '';
      const lineNum = opts.showLineNumbers ? `${i + 1}: ` : '';
      
      output += `${lineNum}${originalLine}\t\t\t\t${lineNum}${modifiedLine}\n`;
    }
    
    return output;
  };

  const generateInlineDiff = (differences: Difference[], opts: ComparisonOptions): string => {
    let output = '';
    
    differences.forEach(diff => {
      const lineNum = opts.showLineNumbers ? `${diff.lineNumber}: ` : '';
      
      switch (diff.type) {
        case 'added':
          output += `${lineNum}[ADDED] ${diff.content}\n`;
          break;
        case 'removed':
          output += `${lineNum}[REMOVED] ${diff.content}\n`;
          break;
        case 'modified':
          output += `${lineNum}[MODIFIED] ${diff.content}\n`;
          break;
        case 'unchanged':
          if (opts.contextLines > 0) {
            output += `${lineNum}${diff.content}\n`;
          }
          break;
      }
    });
    
    return output;
  };

  const generateWordDiff = (original: string, modified: string, opts: ComparisonOptions): string => {
    const originalWords = original.split(/\s+/);
    const modifiedWords = modified.split(/\s+/);
    
    let output = 'Word-by-word comparison:\n\n';
    const maxLength = Math.max(originalWords.length, modifiedWords.length);
    
    for (let i = 0; i < maxLength; i++) {
      const originalWord = originalWords[i] || '';
      const modifiedWord = modifiedWords[i] || '';
      
      if (originalWord !== modifiedWord) {
        if (originalWord && !modifiedWord) {
          output += `[REMOVED] "${originalWord}"\n`;
        } else if (!originalWord && modifiedWord) {
          output += `[ADDED] "${modifiedWord}"\n`;
        } else {
          output += `[CHANGED] "${originalWord}" → "${modifiedWord}"\n`;
        }
      }
    }
    
    return output;
  };

  const generateCharacterDiff = (original: string, modified: string, opts: ComparisonOptions): string => {
    let output = 'Character-by-character comparison:\n\n';
    const maxLength = Math.max(original.length, modified.length);
    
    for (let i = 0; i < maxLength; i++) {
      const originalChar = original[i] || '';
      const modifiedChar = modified[i] || '';
      
      if (originalChar !== modifiedChar) {
        if (originalChar && !modifiedChar) {
          output += `Position ${i}: [REMOVED] "${originalChar}"\n`;
        } else if (!originalChar && modifiedChar) {
          output += `Position ${i}: [ADDED] "${modifiedChar}"\n`;
        } else {
          output += `Position ${i}: [CHANGED] "${originalChar}" → "${modifiedChar}"\n`;
        }
      }
    }
    
    return output;
  };

  const generateContextDiff = (differences: Difference[], opts: ComparisonOptions): string => {
    let output = '';
    const contextLines = opts.contextLines;
    
    differences.forEach((diff, index) => {
      if (diff.type !== 'unchanged') {
        // Add context before
        for (let i = Math.max(0, index - contextLines); i < index; i++) {
          if (differences[i].type === 'unchanged') {
            output += `  ${differences[i].content}\n`;
          }
        }
        
        // Add the difference
        const prefix = diff.type === 'added' ? '+ ' : diff.type === 'removed' ? '- ' : '! ';
        output += `${prefix}${diff.content}\n`;
        
        // Add context after
        for (let i = index + 1; i <= Math.min(differences.length - 1, index + contextLines); i++) {
          if (differences[i].type === 'unchanged') {
            output += `  ${differences[i].content}\n`;
          }
        }
        
        output += '\n';
      }
    });
    
    return output;
  };

  const generateSummary = (stats: ComparisonStats): string => {
    return `Comparison Summary:
    
Total Lines: ${stats.totalLines}
Added Lines: ${stats.addedLines}
Removed Lines: ${stats.removedLines}
Modified Lines: ${stats.modifiedLines}
Unchanged Lines: ${stats.unchangedLines}

Characters Added: ${stats.addedCharacters}
Characters Removed: ${stats.removedCharacters}

Similarity: ${stats.similarityPercentage}%
Word Differences: ${stats.wordDifferences}
Character Differences: ${stats.characterDifferences}`;
  };

  const generateAdditionsOnly = (differences: Difference[], opts: ComparisonOptions): string => {
    const additions = differences.filter(d => d.type === 'added');
    let output = 'Added Content:\n\n';
    
    additions.forEach(diff => {
      const lineNum = opts.showLineNumbers ? `${diff.lineNumber}: ` : '';
      output += `${lineNum}${diff.content}\n`;
    });
    
    return output || 'No additions found.';
  };

  const generateDeletionsOnly = (differences: Difference[], opts: ComparisonOptions): string => {
    const deletions = differences.filter(d => d.type === 'removed');
    let output = 'Removed Content:\n\n';
    
    deletions.forEach(diff => {
      const lineNum = opts.showLineNumbers ? `${diff.lineNumber}: ` : '';
      output += `${lineNum}${diff.content}\n`;
    });
    
    return output || 'No deletions found.';
  };

  const generateModificationsOnly = (differences: Difference[], opts: ComparisonOptions): string => {
    const modifications = differences.filter(d => d.type === 'modified');
    let output = 'Modified Content:\n\n';
    
    modifications.forEach(diff => {
      const lineNum = opts.showLineNumbers ? `${diff.lineNumber}: ` : '';
      output += `${lineNum}${diff.content}\n`;
    });
    
    return output || 'No modifications found.';
  };

  const generateWhitespaceDiff = (original: string, modified: string, opts: ComparisonOptions): string => {
    let output = 'Whitespace Differences:\n\n';
    
    const originalLines = original.split('\n');
    const modifiedLines = modified.split('\n');
    const maxLines = Math.max(originalLines.length, modifiedLines.length);
    
    for (let i = 0; i < maxLines; i++) {
      const originalLine = originalLines[i] || '';
      const modifiedLine = modifiedLines[i] || '';
      
      // Check for whitespace differences
      if (originalLine.trim() === modifiedLine.trim() && originalLine !== modifiedLine) {
        output += `Line ${i + 1}:\n`;
        output += `  Original: "${originalLine}"\n`;
        output += `  Modified: "${modifiedLine}"\n\n`;
      }
    }
    
    return output || 'No whitespace differences found.';
  };

  const generateSemanticDiff = (original: string, modified: string, opts: ComparisonOptions): string => {
    const originalWords = original.toLowerCase().split(/\s+/).filter(w => w);
    const modifiedWords = modified.toLowerCase().split(/\s+/).filter(w => w);
    
    const originalSet = new Set(originalWords);
    const modifiedSet = new Set(modifiedWords);
    
    const addedWords = Array.from(modifiedSet).filter(word => !originalSet.has(word));
    const removedWords = Array.from(originalSet).filter(word => !modifiedSet.has(word));
    const commonWords = Array.from(originalSet).filter(word => modifiedSet.has(word));
    
    let output = 'Semantic Analysis:\n\n';
    
    output += `Common Words (${commonWords.length}): ${commonWords.join(', ')}\n\n`;
    output += `Added Words (${addedWords.length}): ${addedWords.join(', ')}\n\n`;
    output += `Removed Words (${removedWords.length}): ${removedWords.join(', ')}\n\n`;
    
    const semanticSimilarity = Math.round((commonWords.length / Math.max(originalSet.size, modifiedSet.size)) * 100);
    output += `Semantic Similarity: ${semanticSimilarity}%`;
    
    return output;
  };

  const comparisonResult: DifferenceResult = useMemo(() => {
    return performComparison(originalText, modifiedText, selectedMethod, options);
  }, [originalText, modifiedText, selectedMethod, options]);

  useEffect(() => {
    setResult(comparisonResult.formattedOutput);
  }, [comparisonResult]);

  const copyToClipboard = async () => {
    if (result) {
      await navigator.clipboard.writeText(result);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const downloadResult = () => {
    if (result) {
      const summary = `Text Difference Analysis:
Method: ${comparisonMethods.find(m => m.id === selectedMethod)?.name}
Similarity: ${comparisonResult.statistics.similarityPercentage}%
Added Lines: ${comparisonResult.statistics.addedLines}
Removed Lines: ${comparisonResult.statistics.removedLines}
Modified Lines: ${comparisonResult.statistics.modifiedLines}

Detailed Comparison:
${result}`;

      const blob = new Blob([summary], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'text-diff-analysis.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const generateSampleTexts = () => {
    const samples = [
      {
        original: `Hello world!
This is the original text.
It has multiple lines.
Some content here.
Final line.`,
        modified: `Hello world!
This is the modified text.
It has multiple lines.
Some new content here.
Additional line.
Final line.`
      },
      {
        original: `function calculateSum(a, b) {
  return a + b;
}

const result = calculateSum(5, 3);
console.log(result);`,
        modified: `function calculateSum(a, b, c = 0) {
  return a + b + c;
}

const result = calculateSum(5, 3, 2);
console.log('Result:', result);`
      },
      {
        original: `apple
banana
cherry
date
elderberry`,
        modified: `apple
blueberry
cherry
date
fig
grape`
      }
    ];
    
    const sample = samples[Math.floor(Math.random() * samples.length)];
    setOriginalText(sample.original);
    setModifiedText(sample.modified);
  };

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      violet: 'bg-violet-50 border-violet-200 text-violet-800'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-3">
            <GitCompare className="text-teal-600" />
            Advanced Text Difference Checker
          </h1>
          <p className="text-gray-600">Compare texts with multiple diff algorithms and detailed analysis</p>
        </div>

        {/* Text Input Areas */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Text Comparison</h2>
            <div className="flex gap-2">
              <button
                onClick={generateSampleTexts}
                className="flex items-center gap-2 px-3 py-1 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors text-sm"
              >
                <Shuffle size={16} />
                Sample Texts
              </button>
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
              >
                <Settings size={16} />
                Options
              </button>
              <button
                onClick={() => setShowStats(!showStats)}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                Stats
              </button>
              <button
                onClick={() => setShowDifferences(!showDifferences)}
                className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
              >
                <GitCompare size={16} />
                Details
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Original Text</label>
                <div className="text-sm text-gray-500">
                  {originalText.split('\n').length} lines
                </div>
              </div>
              <textarea
                value={originalText}
                onChange={(e) => setOriginalText(e.target.value)}
                placeholder="Enter original text here..."
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none resize-none text-gray-700 transition-colors font-mono text-sm"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Modified Text</label>
                <div className="text-sm text-gray-500">
                  {modifiedText.split('\n').length} lines
                </div>
              </div>
              <textarea
                value={modifiedText}
                onChange={(e) => setModifiedText(e.target.value)}
                placeholder="Enter modified text here..."
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none resize-none text-gray-700 transition-colors font-mono text-sm"
              />
            </div>
          </div>
        </div>

        {/* Comparison Options */}
        {showOptions && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-800">Comparison Options</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Text Processing</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.caseSensitive}
                    onChange={(e) => setOptions({...options, caseSensitive: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Case Sensitive</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.ignoreWhitespace}
                    onChange={(e) => setOptions({...options, ignoreWhitespace: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Ignore Whitespace</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.ignoreEmptyLines}
                    onChange={(e) => setOptions({...options, ignoreEmptyLines: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Ignore Empty Lines</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.trimLines}
                    onChange={(e) => setOptions({...options, trimLines: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Trim Lines</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Comparison Level</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.wordLevel}
                    onChange={(e) => setOptions({...options, wordLevel: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Word Level Comparison</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.characterLevel}
                    onChange={(e) => setOptions({...options, characterLevel: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Character Level Comparison</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.ignoreLeadingWhitespace}
                    onChange={(e) => setOptions({...options, ignoreLeadingWhitespace: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Ignore Leading Whitespace</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.ignoreTrailingWhitespace}
                    onChange={(e) => setOptions({...options, ignoreTrailingWhitespace: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Ignore Trailing Whitespace</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Display Options</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.showLineNumbers}
                    onChange={(e) => setOptions({...options, showLineNumbers: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Show Line Numbers</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.highlightChanges}
                    onChange={(e) => setOptions({...options, highlightChanges: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Highlight Changes</span>
                </label>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Context Lines</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={options.contextLines}
                    onChange={(e) => setOptions({...options, contextLines: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Comparison Methods */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <GitCompare className="text-teal-600" />
            <h2 className="text-xl font-semibold text-gray-800">Choose Comparison Method</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {comparisonMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  selectedMethod === method.id
                    ? `border-${method.color}-500 ${getColorClasses(method.color)} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {method.icon}
                    <h3 className={`font-semibold ${
                      selectedMethod === method.id ? '' : 'text-gray-800'
                    }`}>
                      {method.name}
                    </h3>
                  </div>
                  {selectedMethod === method.id && (
                    <div className={`w-3 h-3 bg-${method.color}-500 rounded-full`}></div>
                  )}
                </div>
                <p className={`text-sm ${
                  selectedMethod === method.id ? '' : 'text-gray-600'
                }`}>
                  {method.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Result Display */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Comparison Result</h2>
            <div className="flex gap-2">
              <button
                onClick={copyToClipboard}
                disabled={!result}
                className="flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50"
              >
                <Copy size={16} />
                {copySuccess ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={downloadResult}
                disabled={!result}
                className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm disabled:opacity-50"
              >
                <Download size={16} />
                Download
              </button>
            </div>
          </div>

          <textarea
            value={result}
            readOnly
            className="w-full h-96 p-4 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-700 resize-none font-mono text-sm"
            placeholder="Comparison result will appear here..."
          />
        </div>

        {/* Statistics */}
        {showStats && (originalText || modifiedText) && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Comparison Statistics</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Similarity</h3>
                <div className="text-2xl font-bold text-blue-900">{comparisonResult.statistics.similarityPercentage}%</div>
                <p className="text-sm text-blue-600">Text similarity</p>
              </div>

              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Added Lines</h3>
                <div className="text-2xl font-bold text-green-900">{comparisonResult.statistics.addedLines}</div>
                <p className="text-sm text-green-600">New content</p>
              </div>

              <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                <h3 className="font-semibold text-red-800 mb-2">Removed Lines</h3>
                <div className="text-2xl font-bold text-red-900">{comparisonResult.statistics.removedLines}</div>
                <p className="text-sm text-red-600">Deleted content</p>
              </div>

              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-semibold text-orange-800 mb-2">Modified Lines</h3>
                <div className="text-2xl font-bold text-orange-900">{comparisonResult.statistics.modifiedLines}</div>
                <p className="text-sm text-orange-600">Changed content</p>
              </div>
            </div>

            {/* Additional Statistics */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Total Lines</h4>
                <div className="text-lg font-bold text-gray-900">{comparisonResult.statistics.totalLines}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Word Differences</h4>
                <div className="text-lg font-bold text-gray-900">{comparisonResult.statistics.wordDifferences}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Character Differences</h4>
                <div className="text-lg font-bold text-gray-900">{comparisonResult.statistics.characterDifferences}</div>
              </div>
            </div>
          </div>
        )}

        {/* Difference Details */}
        {showDifferences && comparisonResult.differences.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <GitCompare className="text-green-600" />
              <h2 className="text-xl font-semibold text-gray-800">Difference Details</h2>
            </div>

            <div className="space-y-3 max-h-64 overflow-y-auto">
              {comparisonResult.differences.filter(d => d.type !== 'unchanged').slice(0, 20).map((diff, index) => (
                <div key={index} className={`p-3 border rounded-lg ${
                  diff.type === 'added' ? 'bg-green-50 border-green-200' :
                  diff.type === 'removed' ? 'bg-red-50 border-red-200' :
                  diff.type === 'modified' ? 'bg-orange-50 border-orange-200' :
                  'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-semibold ${
                      diff.type === 'added' ? 'text-green-700' :
                      diff.type === 'removed' ? 'text-red-700' :
                      diff.type === 'modified' ? 'text-orange-700' :
                      'text-gray-700'
                    }`}>
                      Line {diff.lineNumber} - {diff.type.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-sm font-mono">
                    {diff.type === 'modified' ? (
                      <div>
                        <div className="text-red-700">- {diff.originalLine}</div>
                        <div className="text-green-700">+ {diff.modifiedLine}</div>
                      </div>
                    ) : (
                      <div className={
                        diff.type === 'added' ? 'text-green-700' :
                        diff.type === 'removed' ? 'text-red-700' :
                        'text-gray-700'
                      }>
                        {diff.type === 'added' ? '+ ' : diff.type === 'removed' ? '- ' : '  '}
                        {diff.content}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {comparisonResult.differences.filter(d => d.type !== 'unchanged').length > 20 && (
                <div className="text-sm text-gray-500 text-center py-2">
                  ...and {comparisonResult.differences.filter(d => d.type !== 'unchanged').length - 20} more differences
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TextDifferenceChecker;